import { createContext, useContext, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { journeyApi } from "@/lib/journeyApi";

const AuthContext = createContext(null);

// Where an authenticated customer should land. Paid access requires an
// entitlement; without one there is no destination (they must purchase).
export function destinationFor(me) {
  if (me?.entitled) return me.onboarding_complete ? "/dashboard" : "/onboarding";
  // Owner/Admin authorization is SEPARATE from a paid customer entitlement: a
  // privileged account with no purchase still has a home (the admin area) and
  // must never hit the customer purchase dead-end.
  if (me?.role === "owner" || me?.role === "admin") return "/dashboard/admin/analytics";
  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [entitled, setEntitled] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [role, setRole] = useState("customer");
  const [loading, setLoading] = useState(true);
  const [googleMode, setGoogleMode] = useState("managed");

  const applyMe = useCallback((me) => {
    if (me?.authenticated) {
      setUser(me.user);
      setEntitled(!!me.entitled);
      setOnboardingComplete(!!me.onboarding_complete);
      setRole(me.role || "customer");
    } else {
      setUser(null);
      setEntitled(false);
      setOnboardingComplete(false);
      setRole("customer");
    }
    return me;
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      applyMe(await journeyApi.me());
    } catch (e) {
      const status = e?.response?.status;
      // A real 401/403 means not signed in. A network/5xx blip must NOT masquerade
      // as "signed out" (that looks like lost data), so retry once before giving up.
      if (status === 401 || status === 403) {
        applyMe({ authenticated: false });
      } else {
        await new Promise((r) => setTimeout(r, 600));
        try {
          applyMe(await journeyApi.me());
        } catch {
          applyMe({ authenticated: false });
        }
      }
    } finally {
      setLoading(false);
    }
  }, [applyMe]);

  useEffect(() => {
    // Learn which Google provider is active (The Paycheck Edit's own vs Emergent-managed).
    journeyApi.authConfig().then((c) => setGoogleMode(c?.google_mode || "managed")).catch(() => {});
  }, []);

  useEffect(() => {
    // Returning from the Google OAuth callback: let AuthCallback exchange the
    // session_id and establish the session first (skip the /me probe here).
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const registerEmail = useCallback(async (p) => applyMe(await journeyApi.register(p)), [applyMe]);
  const loginEmail = useCallback(async (p) => applyMe(await journeyApi.login(p)), [applyMe]);
  const exchangeGoogle = useCallback(async (sid) => applyMe(await journeyApi.googleSession(sid)), [applyMe]);
  const linkPurchase = useCallback(async (sid) => applyMe(await journeyApi.linkPurchase(sid)), [applyMe]);
  const refresh = useCallback(async () => applyMe(await journeyApi.me()), [applyMe]);
  const forgot = useCallback((email) => journeyApi.forgot(email), []);
  const resetPassword = useCallback((token, password) => journeyApi.resetPassword(token, password), []);

  const logout = useCallback(async () => {
    try { await journeyApi.logout(); } catch (_) {}
    try { sessionStorage.removeItem("pe_paused_session"); } catch (_) {}
    applyMe({ authenticated: false });
  }, [applyMe]);

  const googleRedirect = useCallback(async (intent, pendingSessionId) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (googleMode === "unavailable") {
      // Production without a valid The Paycheck Edit Google client: fail safe. Never
      // redirect to the Emergent-managed provider; keep email/password available.
      toast.error("Google sign-in is temporarily unavailable. Please sign in with email instead.");
      return;
    }
    if (googleMode === "byo") {
      // The Paycheck Edit's own Google client: start the server-side flow (the backend
      // redirects to Google's branded consent and handles the callback + linking).
      // Pre-flight the backend so a transient /api proxy or startup race shows a
      // friendly message instead of dropping the customer on a raw 404 page.
      try {
        const c = await journeyApi.authConfig();
        if (!c?.google_enabled) {
          toast.error("Google sign-in couldn't start. Please try again or sign in with email.");
          return;
        }
      } catch (_) {
        toast.error("Google sign-in couldn't start. Please try again or sign in with email.");
        return;
      }
      const base = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
      const qs = new URLSearchParams({ intent: intent || "login" });
      if (pendingSessionId) qs.set("session_id", pendingSessionId);
      window.location.href = `${base}?${qs.toString()}`;
      return;
    }
    // Emergent-managed fallback (until the BYO client credentials are configured).
    try {
      sessionStorage.setItem("ms_auth_intent", intent || "login");
      if (pendingSessionId) sessionStorage.setItem("ms_pending_purchase", pendingSessionId);
      else sessionStorage.removeItem("ms_pending_purchase");
    } catch (_) {}
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, [googleMode]);

  const value = {
    user, entitled, onboardingComplete, role, loading, googleMode,
    registerEmail, loginEmail, exchangeGoogle, linkPurchase, refresh,
    logout, forgot, resetPassword, googleRedirect,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
